import { t } from '@lingui/core/macro';
import { BuyerRoomDto } from '@/services/rooms';
import { ConfiguratorGroupDto, ConfiguratorRoomDto } from '@/services/configurator';

/** Synthetic "General" bucket: groups whose backend roomId is null are collected under this id. */
export const GENERAL_ROOM_ID = 0;

/**
 * Single source of truth for a room's display label. The General bucket carries no name, so
 * consumers must not render `roomName` directly — use this instead.
 */
export function getRoomDisplayName(room: Pick<ConfiguratorRoomDto, 'roomId' | 'roomName'>): string {
    return room.roomId === GENERAL_ROOM_ID ? t`General options` : room.roomName;
}

/**
 * Reconstructs the room-grouped view from the flat option-group list. The id-0 "General"
 * bucket collects groups whose backend roomId is null and is always sorted last. Real rooms
 * are sorted by their metadata `sortOrder` (falling back to room id) so order is stable
 * regardless of the flat list's order.
 */
export function groupConfiguratorRooms(
    groups: ConfiguratorGroupDto[],
    roomsMap: Map<number, BuyerRoomDto> | null
): ConfiguratorRoomDto[] {
    const byRoom = new Map<number, ConfiguratorGroupDto[]>();
    for (const group of groups) {
        const roomId = group.roomId ?? GENERAL_ROOM_ID;
        const bucket = byRoom.get(roomId);
        if (bucket) {
            bucket.push(group);
        } else {
            byRoom.set(roomId, [group]);
        }
    }

    const rooms: ConfiguratorRoomDto[] = [];
    byRoom.forEach((roomGroups, roomId) => {
        const meta = roomId === GENERAL_ROOM_ID ? undefined : roomsMap?.get(roomId);
        rooms.push({
            roomId,
            // General (id 0) carries no name/icon; render its label via getRoomDisplayName.
            roomName: meta?.name ?? '',
            icon: meta?.icon ?? null,
            groups: roomGroups,
            roomPrice: roomGroups.reduce((sum, g) => sum + (g.selectedOption?.price?.finalPrice ?? 0), 0),
        });
    });

    const sortKey = (room: ConfiguratorRoomDto) => {
        if (room.roomId === GENERAL_ROOM_ID) return Number.MAX_SAFE_INTEGER;
        return roomsMap?.get(room.roomId)?.sortOrder ?? room.roomId;
    };
    return rooms.sort((a, b) => sortKey(a) - sortKey(b));
}
